import localForageHelper from '@helpers/localforage.helper'
import User from '@interfaces/user.interface'
import onGeneralLoaderChange from '@observables/general-loader.observable'
import onUserChange from '@observables/user.observable'
import { take } from 'rxjs/operators'

interface UserObject {
  JWT: string
  expiresAt: number
  userInfo: User
  updatedAt: number
}

const USER_KEY = 'hub_user_session_info'
const ALLOWED_WITHOUT_USER = ['login', 'error', 'contract']

export default class UserHelper {
  async getUserObject(): Promise<UserObject> {
    return await localForageHelper.getItem(USER_KEY)
  }

  async setUserObject(userInfo: User, JWT: string, expiresAt: number): Promise<UserObject> {
    const object: UserObject = {
      JWT,
      expiresAt,
      userInfo,
      // Set updatedAt to the current timestamp to ensure data freshness
      updatedAt: new Date().getTime(),
    }
    onUserChange.next(userInfo)
    await localForageHelper.setItem(USER_KEY, object)
    return object
  }

  async getUser(): Promise<User> {
    return new Promise((resolve) => {
      onUserChange
        .pipe(take(1))
        .subscribe(async (user: User) => {
          onGeneralLoaderChange.next(!user)
          const userObject: UserObject = await localForageHelper.getItem(USER_KEY)
          if (new Date().getTime() > userObject?.expiresAt * 1000 || !userObject?.userInfo) {
            this.deleteUser()
            if (!ALLOWED_WITHOUT_USER.includes(window.location.pathname.split('/')[1] || '')) {
              await localForageHelper.setItem('follow_link', `${window.location.pathname}${window.location.search}`)
              history.pushState({}, '', '/login')
            }
            onGeneralLoaderChange.next(false)
            resolve(null)
          }
          if (!user && userObject?.userInfo) {
            onUserChange.next(userObject?.userInfo)
          }
          onGeneralLoaderChange.next(false)
          resolve(userObject?.userInfo)
        })
        .unsubscribe()
    })
  }

  async setUser(userInfo: User): Promise<User> {
    onUserChange.next(userInfo)
    const userObject: UserObject = await localForageHelper.getItem(USER_KEY)
    localForageHelper.setItem(USER_KEY, {
      ...userObject,
      userInfo,
    })
    return userInfo
  }

  deleteUser(): void {
    localForageHelper.removeItem(USER_KEY)
    localForageHelper.removeItem('impersonateUser')
    localForageHelper.removeItem('fingerprint')
    onUserChange.next(null)
  }
}
